import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getProducts, updateProductScreen, updateTimer } from '../../services/productService';
import './Products.css';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function ProductsList() {
  const [products, setProducts] = useState([]);
  const [screens, setScreens] = useState([]);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [nameFilter, setNameFilter] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [showTimerPopup, setShowTimerPopup] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newMinutes, setNewMinutes] = useState('0');
  const [newSeconds, setNewSeconds] = useState('0');
  const [loading, setLoading] = useState(false);
 
  const debouncedNameFilter = useDebounce(nameFilter, 500);

  useEffect(() => {
    setLoading(true);
    getProducts(page, size, debouncedNameFilter)
      .then((data) => {
        setProducts(data.products.items);
        setScreens(data.screens);
        setTotalPages(data.products.pages);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Get product error', err);
        setLoading(false);
      });
  }, [page, size, debouncedNameFilter]);

  const handleScreenToggle = useCallback(async (productId, screenId, currentIsActive) => {
    try {
      setProducts((prev) =>
        prev.map((product) => {
          if (product.id === productId) {
            return {
              ...product,
              product_screens: currentIsActive
                ? product.product_screens.filter((ps) => ps.screen.id !== screenId)
                : [
                    ...product.product_screens,
                    {
                      screen: {
                        id: screenId,
                        name: screens.find((screen) => screen.id === screenId)?.name,
                      },
                      is_active: true,
                    },
                  ],
            };
          }
          return product;
        })
      );

      await updateProductScreen(productId, screenId, !currentIsActive);
    } catch (error) {
      console.error('Cannot update', error);
    }
  }, [screens]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  }, [totalPages]);

  const handlePageSizeChange = useCallback((newSize) => {
    setSize(newSize);
    setPage(1);
  }, []);

  const getPaginationRange = useCallback(() => {
    const range = [];
    const maxPages = 5;

    let startPage = Math.max(page - 2, 1);
    let endPage = Math.min(page + 2, totalPages);

    if (endPage - startPage < maxPages - 1) {
      if (page < totalPages / 2) {
        endPage = Math.min(startPage + maxPages - 1, totalPages);
      } else {
        startPage = Math.max(endPage - maxPages + 1, 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    return range;
  }, [page, totalPages]);

  const handleTimerClick = useCallback((product) => {
    setSelectedProduct(product);

    const [, minutes, seconds] = product.timer
      ? product.timer.split(':').map((v) => parseInt(v, 10))
      : [0, 0, 0];

    setNewMinutes(minutes.toString());
    setNewSeconds(seconds.toString());
    setShowTimerPopup(true);
  }, []);

  const handleTimerUpdate = useCallback(async () => {
    if (!selectedProduct) return;

    try {
      const formattedTimer = `00:${newMinutes.padStart(2, '0')}:${newSeconds.padStart(2, '0')}`;
      await updateTimer(selectedProduct.id, formattedTimer);

      setProducts((prev) =>
        prev.map((product) =>
          product.id === selectedProduct.id
            ? { ...product, timer: formattedTimer }
            : product
        )
      );

      setShowTimerPopup(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Cannot update timer', error);
    }
  }, [selectedProduct, newMinutes, newSeconds]);

  const handlePopupClose = useCallback(() => {
    setShowTimerPopup(false);
    setSelectedProduct(null);
  }, []);

  return (
    <div className="products-container">
      <div className="products-header">
        <div className="products-header-content">
          <h1 className="products-title">🍔 Продукты</h1>
          <p className="products-subtitle">Управление меню и экранами</p>
        </div>
      </div>

      {/* Filters */}
      <div className="products-filters">
        <div className="products-search">
          <span className="products-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Фильтр по названию..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            className="products-search-input"
          />
        </div>
        <div className="products-page-size">
          <label>Размер страницы:</label>
          <select 
            value={size} 
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className="products-select"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="products-loading">
          <div className="products-loading-spinner"></div>
          <p>Загрузка продуктов...</p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="products-table-wrapper">
            <table className="products-table">
              <thead>
                <tr>
                  <th className="product-name-col">Название продукта</th>
                  <th className="timer-col">Таймер</th>
                  {screens.map((screen) => (
                    <th key={screen.id} className="screen-col">{screen.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td className="product-name-col">
                      <Link to={`/modifiers/${product.id}`} className="product-link">
                        {product.name}
                      </Link>
                    </td>
                    <td className="timer-col">
                      <button
                        className="timer-btn"
                        onClick={() => handleTimerClick(product)}
                      >
                        ⏱️ {product.timer ? product.timer.split(':').slice(1).join(':') : '00:00'}
                      </button>
                    </td>
                    {screens.map((screen) => {
                      const isActive = product.product_screens.some(
                        (ps) => ps.screen.id === screen.id && ps.is_active
                      );
                      return (
                        <td key={screen.id} className="screen-col">
                          <label className="checkbox-wrapper">
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={() =>
                                handleScreenToggle(product.id, screen.id, isActive)
                              }
                              className="checkbox-input"
                            />
                            <span className="checkbox-custom"></span>
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="products-pagination">
            <button
              className="pagination-btn"
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              ← Назад
            </button>
            <div className="pagination-pages">
              {getPaginationRange().map((pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`pagination-page ${page === pageNumber ? 'active' : ''}`}
                >
                  {pageNumber}
                </button>
              ))}
            </div>
            <button
              className="pagination-btn"
              disabled={page >= totalPages}
              onClick={() => handlePageChange(page + 1)}
            >
              Далее →
            </button>
          </div>
        </>
      )}

      {/* Timer Popup */}
      {showTimerPopup && (
        <div className="timer-overlay" onClick={handlePopupClose}>
          <div className="timer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="timer-modal-header">
              <h2 className="timer-modal-title">⏱️ Установить таймер</h2>
              <p className="timer-modal-product">{selectedProduct?.name}</p>
            </div>
            <div className="timer-inputs">
              <div className="timer-input-group">
                <label>Минуты</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={newMinutes}
                  onChange={(e) => setNewMinutes(e.target.value)}
                  className="timer-input"
                />
              </div>
              <span className="timer-separator">:</span>
              <div className="timer-input-group">
                <label>Секунды</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={newSeconds}
                  onChange={(e) => setNewSeconds(e.target.value)}
                  className="timer-input"
                />
              </div>
            </div>
            <div className="timer-modal-actions">
              <button className="timer-modal-btn cancel" onClick={handlePopupClose}>
                Отмена
              </button>
              <button className="timer-modal-btn confirm" onClick={handleTimerUpdate}>
                Обновить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductsList;
